import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from "react";

interface ElementSize {
  width: number;
  height: number;
}

type ElementEvents = keyof HTMLElementEventMap;
type WindownEvents = keyof WindowEventMap;
type MediaEvents = keyof MediaQueryListEventMap;
export function useEventListener<T extends HTMLElement>(
  eventName: ElementEvents | WindownEvents | MediaEvents,
  handler: (
    event:
      | WindowEventMap[WindownEvents]
      | HTMLElementEventMap[ElementEvents]
      | MediaQueryListEventMap[MediaEvents]
      | Event,
  ) => void,
  element?: RefObject<T>,
) {
  const savedHandler = useRef(handler);
  useLayoutEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Define the listening target
    const targetElement: T | Window = element?.current ?? window;
    if (!(targetElement && targetElement.addEventListener)) return;
    // Create event listener that calls handler function stored in ref
    const listener: typeof handler = (event) => savedHandler.current(event);
    targetElement.addEventListener(eventName, listener);
    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, listener);
    };
  }, [eventName, element]);
}

export function useElementSize<T extends HTMLElement>(): [
  (node: T | null) => void,
  ElementSize,
  T | null,
] {
  const [ref, setRef] = useState<T | null>(null);
  const [size, setSize] = useState<ElementSize>({
    width: 0,
    height: 0,
  });
  const handleSize = useCallback(() => {
    setSize({
      width: ref?.clientWidth ?? 0,
      height: ref?.clientHeight ?? 0,
    });
  }, [ref?.clientWidth, ref?.clientHeight]);
  useEventListener("resize", handleSize);
  useLayoutEffect(() => {
    // TODO: too many changes may lead to crash
    handleSize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref?.clientWidth, ref?.clientHeight]);
  return [setRef, size, ref];
}
